<script lang="ts">
// An icon asked for by meaning (ADR 0096): `<LumiIcon name="delete" class="h-4 w-4" />`.
// The glyph comes from the vocabulary; sizing, colour and every other attribute reach
// the rendered svg exactly as on a Heroicons component (single root, the glyph's own
// aria-hidden kept). `on` selects the filled variant for the meanings that have one
// (today: favourite).
//
// Why the svg is cloned rather than mounted: a Heroicons glyph is a prop-less
// functional component, and Vue hands such a component only `class`, `style` and
// `onXxx` from its fallthrough attributes — `width`, `height` and `stroke-width` would
// be dropped silently. So the glyph's vnode is taken here and the attributes are merged
// onto it directly.
//
// Why the clone carries a key: a glyph's svg is a compiled block whose paths are
// hoisted static, so patching one glyph into another of the same root keeps the old
// paths. Keyed by meaning, a changed `name` (or `on`) replaces the svg instead.
import { cloneVNode, defineComponent } from 'vue';
import type { Component, FunctionalComponent, PropType } from 'vue';
import { LUMI_ICONS, LUMI_ICONS_ON } from './vocabulary';
import type { LumiIconName } from './vocabulary';

export default defineComponent({
  name: 'LumiIcon',
  inheritAttrs: false,
  props: {
    /** The meaning to draw. */
    name: { type: String as PropType<LumiIconName>, required: true },
    /** The filled variant of the meaning, where it has one. */
    on: { type: Boolean, default: false },
  },
  setup(props, { attrs }) {
    return () => {
      const glyph: Component = (props.on && LUMI_ICONS_ON[props.name]) || LUMI_ICONS[props.name];
      const vnode = (glyph as FunctionalComponent)({}, { attrs: {}, slots: {}, emit: () => {} });
      return cloneVNode(vnode as never, { ...attrs, key: `${props.name}:${props.on}` });
    };
  },
});
</script>
